// ============================================================================
// 影像載入失敗的成因判定
//
// `<img @error>` 只告訴你「載不到」，不告訴你為什麼。這兩件事的處置完全不同：
//
//   404          檔案真的不在了（有人手動刪了 upload/image/、備份還原時漏了、
//                磁碟壞軌）。這是永久狀態，可以據此改資料（取消勾選列印）。
//   其他 / 拋錯  server 沒起來、nginx 重啟中、網路瞬斷、認證過期。這是暫時
//                狀態，**絕對不能**據此改資料——一次網路抖動就會把醫師選好的
//                報告影像全部取消勾選，而且畫面上看起來像是自己變的。
//
// 所以流程是：`@error` 當作免費的觸發器（正常情況下一次都不會發生），真的觸發
// 了再打一次 HEAD 把狀態碼問出來。成本只在出事時才付。
// ============================================================================

/** @typedef {'missing'|'error'} ImageProbeResult */

/**
 * 問清楚這張圖到底是「不存在」還是「這次拿不到」。
 *
 * @param {string} url 與 <img src> 同一個網址
 * @returns {Promise<ImageProbeResult>} 'missing' 只在確定收到 404 時回傳
 */
export async function probeImage(url) {
  try {
    // same-origin：/api/upload/... 走的是同一個 nginx，要帶 session cookie，
    // 否則會拿到 401 而不是 404，於是被判成 'error'（保守，但就漏掉了真正的遺失）
    const res = await fetch(url, { method: 'HEAD', credentials: 'same-origin' })
    return res.status === 404 ? 'missing' : 'error'
  } catch (e) {
    // fetch 直接拋 = 網路層就失敗（server 沒起來、DNS、CORS）。
    // 這種情況下我們對檔案存在與否一無所知，一律當成暫時性錯誤。
    return 'error'
  }
}
